import { type FormEvent, type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { getFirebase } from "../firebase/init.js";
import { applyWriteMap } from "../firebase/writes.js";
import {
  type EditableCharacter,
  type ExtraRecord,
  removeExtra,
  setExtra,
} from "../lib/character-edits.js";

const KINDS: ExtraRecord["kind"][] = ["companion", "gear", "vehicle", "power", "other"];

interface Props {
  characterPath: string;
  character: EditableCharacter;
}

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);
}

export default function CharacterExtrasEditor({ characterPath, character }: Props): ReactElement {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<{ name: string; kind: ExtraRecord["kind"]; notes: string }>({
    name: "",
    kind: "gear",
    notes: "",
  });

  async function writeExtra(extra: ExtraRecord): Promise<void> {
    const { db } = getFirebase();
    await applyWriteMap(db, setExtra(characterPath, extra));
  }

  async function handleAdd(e: FormEvent): Promise<void> {
    e.preventDefault();
    const slug = slugify(draft.name);
    if (!slug) return;
    await writeExtra({
      id: slug,
      name: draft.name.trim(),
      kind: draft.kind,
      ...(draft.notes.trim() ? { notes: draft.notes.trim() } : {}),
      counters: [],
    });
    setDraft({ name: "", kind: "gear", notes: "" });
  }

  async function handleRemove(id: string): Promise<void> {
    if (!window.confirm(t("edit.confirm.delete-extra"))) return;
    const { db } = getFirebase();
    await applyWriteMap(db, removeExtra(characterPath, id));
  }

  async function bumpCounter(extra: ExtraRecord, counterId: string, delta: number): Promise<void> {
    const counters = (extra.counters ?? []).map((c) =>
      c.id === counterId ? { ...c, current: Math.max(0, Math.min(c.max, c.current + delta)) } : c,
    );
    await writeExtra({ ...extra, counters });
  }

  async function addCounter(extra: ExtraRecord, label: string, max: number): Promise<void> {
    const counters = [
      ...(extra.counters ?? []),
      { id: slugify(label), labelKey: label, current: 0, max },
    ];
    await writeExtra({ ...extra, counters });
  }

  async function removeCounter(extra: ExtraRecord, counterId: string): Promise<void> {
    const counters = (extra.counters ?? []).filter((c) => c.id !== counterId);
    await writeExtra({ ...extra, counters });
  }

  const extras = Object.values(character.extras ?? {});

  return (
    <section>
      <h2 className="text-sm font-semibold uppercase text-muted-foreground">{t("edit.extras")}</h2>
      <ul className="mt-2 space-y-3">
        {extras.map((e) => (
          <li key={e.id} className="rounded border p-2 text-sm">
            <div className="flex items-start gap-2">
              <div className="flex-1">
                <span className="font-medium">{e.name}</span>{" "}
                <span className="text-xs text-muted-foreground">
                  · {t(`edit.extra.kind.${e.kind}`)}
                </span>
                {e.notes && <p className="mt-1 text-xs text-muted-foreground">{e.notes}</p>}
              </div>
              <button
                type="button"
                className="rounded border border-rose-400 px-2 py-0.5 text-xs text-rose-700"
                onClick={() => handleRemove(e.id)}
              >
                {t("edit.delete")}
              </button>
            </div>
            <ul className="mt-2 space-y-1">
              {(e.counters ?? []).map((c) => (
                <li key={c.id} className="flex items-center gap-2 text-xs">
                  <span className="flex-1">{c.labelKey}</span>
                  <button
                    type="button"
                    aria-label={`${t("edit.counter.decrement")} ${c.labelKey}`}
                    className="rounded border px-1"
                    onClick={() => bumpCounter(e, c.id, -1)}
                  >
                    −
                  </button>
                  <span className="font-mono">
                    {c.current}/{c.max}
                  </span>
                  <button
                    type="button"
                    aria-label={`${t("edit.counter.increment")} ${c.labelKey}`}
                    className="rounded border px-1"
                    onClick={() => bumpCounter(e, c.id, +1)}
                  >
                    +
                  </button>
                  <button
                    type="button"
                    aria-label={`${t("edit.delete")} ${c.labelKey}`}
                    className="rounded border border-rose-400 px-1 text-rose-700"
                    onClick={() => removeCounter(e, c.id)}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
            <CounterAddForm
              onAdd={(label, max) => {
                void addCounter(e, label, max);
              }}
            />
          </li>
        ))}
      </ul>

      <form onSubmit={handleAdd} className="mt-3 grid grid-cols-[1fr_auto_1fr_auto] gap-2 text-sm">
        <input
          className="rounded border px-2 py-1"
          placeholder={t("edit.extra.name-placeholder")}
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          aria-label={t("edit.extra.new-name")}
        />
        <select
          className="rounded border px-2 py-1"
          value={draft.kind}
          onChange={(e) => setDraft({ ...draft, kind: e.target.value as ExtraRecord["kind"] })}
          aria-label={t("edit.extra.new-kind")}
        >
          {KINDS.map((k) => (
            <option key={k} value={k}>
              {t(`edit.extra.kind.${k}`)}
            </option>
          ))}
        </select>
        <input
          className="rounded border px-2 py-1"
          placeholder={t("edit.extra.notes-placeholder")}
          value={draft.notes}
          onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
          aria-label={t("edit.extra.new-notes")}
        />
        <button type="submit" className="rounded border px-2 py-1">
          {t("edit.extra.add")}
        </button>
      </form>
    </section>
  );
}

function CounterAddForm({
  onAdd,
}: {
  onAdd: (label: string, max: number) => void;
}): ReactElement {
  const { t } = useTranslation();
  const [label, setLabel] = useState("");
  const [max, setMax] = useState("3");
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const m = Number.parseInt(max, 10);
        if (label.trim().length === 0 || Number.isNaN(m) || m < 1) return;
        onAdd(label.trim(), m);
        setLabel("");
        setMax("3");
      }}
      className="mt-2 flex gap-1 text-xs"
    >
      <input
        className="flex-1 rounded border px-1 py-0.5"
        placeholder={t("edit.counter.label-placeholder")}
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        aria-label={t("edit.counter.new-label")}
      />
      <input
        type="number"
        min={1}
        max={20}
        className="w-14 rounded border px-1 py-0.5"
        value={max}
        onChange={(e) => setMax(e.target.value)}
        aria-label={t("edit.counter.new-max")}
      />
      <button type="submit" className="rounded border px-1 py-0.5">
        {t("edit.counter.add")}
      </button>
    </form>
  );
}
